// src/visitorout/VisitorOutList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import 'bootstrap/dist/css/bootstrap.min.css';
import { VISITOR_OUT_LIST_API, VISITOR_OUT_UPDATE_API } from '../utils/api';

function VisitorOutList() {
    const [loading, setLoading] = useState(false);
    const [visitors, setVisitors] = useState([]);
    const [search, setSearch] = useState('');

    const token = localStorage.getItem('token') || '';
    let customer_id = null;
    try {
        const parsed = JSON.parse(atob(token));
        customer_id = parsed?.customer_id || parsed?.company_id || null;
    } catch {}

    const fetchPendingOut = async () => {
        if (!customer_id) {
            toast.error('Customer ID not found in token');
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(
                VISITOR_OUT_LIST_API,
                { customer_id, token },
                { headers: { Authorization: token } }
            );

            if (res.data?.success) {
                setVisitors(res.data.visitors || []);
            } else {
                toast.error(res.data?.message || 'Failed to load visitors');
            }
        } catch (err) {
            console.error('❌ visitor-out-list error:', err);
            toast.error('Server error while loading visitors');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchPendingOut(); }, []);

    const toLocal = (dt) => {
        try {
            if (!dt) return '-';
            const iso = dt.includes('T') ? dt : dt.replace(' ', 'T');
            const d = new Date(iso);
            if (isNaN(d.getTime())) return dt;
            return d.toLocaleString();
        } catch {
            return dt || '-';
        }
    };

    const getTeamCount = (v) => {
        if (Number.isFinite(v?.team_members_count)) return v.team_members_count;
        const raw = (v?.team_members ?? '').toString().trim();
        if (!raw) return 0;
        if (/^\d+$/.test(raw)) return parseInt(raw, 10) || 0;
        return raw.split(',').map(s => s.trim()).filter(Boolean).length;
    };

    const getHeadcount = (v) => 1 + getTeamCount(v);

    // 📌 OUT Entry Function
    const markVisitorOut = async (visitor_id) => {
        if (!window.confirm('Are you sure you want to mark this visitor as OUT?')) return;
        try {
            const res = await axios.post(
                VISITOR_OUT_UPDATE_API,
                { visitor_id, token },
                { headers: { Authorization: token } }
            );

            if (res.data?.success) {
                toast.success('Visitor marked OUT successfully');
                fetchPendingOut();
            } else {
                toast.error(res.data?.message || 'Failed to mark OUT');
            }
        } catch (err) {
            console.error('❌ visitor-out-update error:', err);
            toast.error('Server error while marking OUT');
        }
    };

    // 📊 Filtered Visitors
    const filteredVisitors = useMemo(() => {
        if (!search.trim()) return visitors;
        const q = search.toLowerCase();
        return visitors.filter(v =>
            (v.gate_pass_no || '').toLowerCase().includes(q) ||
            (v.name || '').toLowerCase().includes(q) ||
            (v.company_name || '').toLowerCase().includes(q) ||
            (v.mobile || '').toLowerCase().includes(q)
        );
    }, [search, visitors]);

    // 📊 Totals & Summaries
    const totals = useMemo(() => {
        let totalPeople = 0;
        const deptMap = new Map();
        const visitToMap = new Map();
        const purposeMap = new Map();
        const add = (map, key, n) => map.set(key, (map.get(key) || 0) + n);

        for (const v of filteredVisitors) {
            const heads = getHeadcount(v);
            totalPeople += heads;
            add(deptMap, v.department_name || '—', heads);
            add(visitToMap, v.whom_to_visit_name || '—', heads);
            add(purposeMap, v.visit_purpose_name || '—', heads);
        }

        const sortArr = (map) =>
            Array.from(map, ([name, count]) => ({ name, count }))
                .sort((a, b) => b.count - a.count);

        return {
            totalPeople,
            depts: sortArr(deptMap),
            visits: sortArr(visitToMap),
            purposes: sortArr(purposeMap),
        };
    }, [filteredVisitors]);

    return (
        <>
            <Navbar />

            <div className="container py-4">
                <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                    <h4 className="mb-2 mb-md-0">🚪 Pending Visitor OUT</h4>
                    <div className="d-flex gap-2">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by Gate Pass, Name, Company, Mobile"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ minWidth: '250px' }}
                        />
                        <button
                            className="btn btn-outline-primary"
                            onClick={fetchPendingOut}
                            disabled={loading}
                        >
                            {loading ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>
                </div>

                {/* Total people info */}
                <div className="alert alert-info py-2 mb-3 shadow-sm rounded-3">
                    <strong>Total People Inside: {totals.totalPeople}</strong>
                    <span className="ms-2 text-muted">(visitor + team members)</span>
                </div>

                {/* Summary Cards */}
                <div className="row g-3 mb-4">
                    {/* Department Breakdown */}
                    <div className="col-md-4">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-light fw-bold">By Department</div>
                            <ul className="list-group list-group-flush">
                                {totals.depts.length > 0 ? (
                                    totals.depts.map((d, i) => (
                                        <li key={i} className="list-group-item d-flex justify-content-between">
                                            <span>{d.name}</span>
                                            <span className="fw-bold">{d.count}</span>
                                        </li>
                                    ))
                                ) : (
                                    <li className="list-group-item text-muted">No data</li>
                                )}
                            </ul>
                        </div>
                    </div>

                    {/* Visit-To Breakdown */}
                    <div className="col-md-4">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-light fw-bold">By Visit To</div>
                            <ul className="list-group list-group-flush">
                                {totals.visits.length > 0 ? (
                                    totals.visits.map((d, i) => (
                                        <li key={i} className="list-group-item d-flex justify-content-between">
                                            <span>{d.name}</span>
                                            <span className="fw-bold">{d.count}</span>
                                        </li>
                                    ))
                                ) : (
                                    <li className="list-group-item text-muted">No data</li>
                                )}
                            </ul>
                        </div>
                    </div>

                    {/* Purpose Breakdown */}
                    <div className="col-md-4">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-light fw-bold">By Purpose</div>
                            <ul className="list-group list-group-flush">
                                {totals.purposes.length > 0 ? (
                                    totals.purposes.map((d, i) => (
                                        <li key={i} className="list-group-item d-flex justify-content-between">
                                            <span>{d.name}</span>
                                            <span className="fw-bold">{d.count}</span>
                                        </li>
                                    ))
                                ) : (
                                    <li className="list-group-item text-muted">No data</li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="card border-0 shadow-sm">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>#</th>
                                    <th>Photo</th>
                                    <th>Gate Pass</th>
                                    <th>Visitor</th>
                                    <th>Company</th>
                                    <th>Mobile</th>
                                    <th>Dept</th>
                                    <th>Visit to</th>
                                    <th>Purpose</th>
                                    <th>IN Time</th>
                                    <th>Team (heads)</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading && filteredVisitors.length === 0 && (
                                    <tr><td colSpan="12" className="text-muted">Loading…</td></tr>
                                )}

                                {!loading && filteredVisitors.length === 0 && (
                                    <tr>
                                        <td colSpan="12" className="text-center text-muted py-4">
                                            No pending gate passes match your search.
                                        </td>
                                    </tr>
                                )}

                                {filteredVisitors.map((v, idx) => (
                                    <tr key={`${v.id}-${v.in_time || idx}`}>
                                        <td>{idx + 1}</td>
                                        <td>
                                            {v.visitor_photo && v.photo_bytes > 0 ? (
                                                <img
                                                    src={`data:${v.visitor_photo_mime || 'image/jpeg'};base64,${v.visitor_photo}`}
                                                    alt={v.name || 'Visitor'}
                                                    width="80"
                                                    height="80"
                                                    style={{ objectFit: 'cover', borderRadius: '8px' }}
                                                />
                                            ) : <span className="text-muted">No Photo</span>}
                                        </td>
                                        <td>{v.gate_pass_no || '—'}</td>
                                        <td>{v.name || '—'}</td>
                                        <td>{v.company_name || '—'}</td>
                                        <td>{v.mobile || '—'}</td>
                                        <td>{v.department_name || '—'}</td>
                                        <td>{v.whom_to_visit_name || '—'}</td>
                                        <td>{v.visit_purpose_name || '—'}</td>
                                        <td>{toLocal(v.in_time)}</td>
                                        <td>{getHeadcount(v)}</td>
                                        <td>
                                            <button
                                                className="btn btn-sm btn-danger"
                                                onClick={() => markVisitorOut(v.id)}
                                            >
                                                OUT Entry
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </>
    );
}

export default VisitorOutList;
